import { createApi } from '@reduxjs/toolkit/query/react';
import { CreatePayOrderDto, CreateTransferPayOrderDto, IPagedCollection, IPagingData, PayOrderDto, TransferPayOrderDto, UpdatePayOrderDateDto, UpdatePayOrderDto, UpdatePayOrderStatusDto } from '@sr/dto';
import { PAY_ORDERS_BASE_URL } from 'shared/api/api';
import { CollectionParams, createBaseQuery } from 'shared/api/rtk-query';
import { pagingDataToParams } from 'utils/remote-paged-collection.hook';
import { refreshInvoicesAfterPayment } from './refresh-invoice-queries';

export const payOrdersApi = createApi({
  reducerPath: 'pay-orders',
  tagTypes: ['pay-orders'],
  baseQuery: createBaseQuery(PAY_ORDERS_BASE_URL),
  endpoints: (builder) => ({
    updateDatePayOrder: builder.mutation<PayOrderDto, UpdatePayOrderDateDto>({
      onQueryStarted: refreshInvoicesAfterPayment,
      query: ({ ...dto }) => ({
        url: '/update-date',
        body: dto,
        method: 'PUT'
      }),
      invalidatesTags: (result, error, query) => [{ type: 'pay-orders', id: query.id }]
    }),
  
    updatePayOrderStatus: builder.mutation<PayOrderDto, UpdatePayOrderStatusDto>({
      onQueryStarted: refreshInvoicesAfterPayment,
      query: ({ ...dto }) => ({
        url: '/update-status',
        method: 'PATCH',
        body: dto
      }),
      invalidatesTags: (result, error, query) => [{ type: 'pay-orders', id: query.id }]
    }),

    getPayOrders: builder.query<IPagedCollection<PayOrderDto>, CollectionParams>({
      query: ({ paging, filter }) => ({
        params: paging && pagingDataToParams(paging),
        url: `?${filter}`,
        method: 'GET'
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.items.map(({ id }) => ({ type: 'pay-orders' as const, id })),
            { type: 'pay-orders', id: 'LIST' }
          ]
          : [{ type: 'pay-orders', id: 'LIST' }]
    }),

    getPayOrder: builder.query<PayOrderDto, number>({
      query: (id) => `/${id}`,
      providesTags: (result, error, id) => [{ type: 'pay-orders', id }]
    }),

    createPayOrder: builder.mutation<PayOrderDto, CreatePayOrderDto>({
      onQueryStarted: refreshInvoicesAfterPayment,
      query: (dto) => ({
        url: '',
        method: 'POST',
        body: dto
      }),
      invalidatesTags: [{ type: 'pay-orders', id: 'LIST' }]
    }),

    createTransferPayOrder: builder.mutation<TransferPayOrderDto, CreateTransferPayOrderDto>({
      query: (dto) => ({
        url: '/transfer',
        method: 'POST',
        body: dto
      }),
      invalidatesTags: [{ type: 'pay-orders', id: 'LIST' }]
    }),

    updatePayOrder: builder.mutation<PayOrderDto, UpdatePayOrderDto>({
      onQueryStarted: refreshInvoicesAfterPayment,
      query: (dto) => ({
        url: '',
        method: 'PUT',
        body: dto
      }),
      invalidatesTags: (result, error, dto) => [
        { type: 'pay-orders', id: dto.id },
        { type: 'pay-orders', id: 'LIST' }
      ]
    }),

    deletePayOrder: builder.mutation<void, number>({
      onQueryStarted: refreshInvoicesAfterPayment,
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'pay-orders', id },
        { type: 'pay-orders', id: 'LIST' }
      ]
    }),

    getInvoiceRelatedPayOrders: builder.query<PayOrderDto[], number>({
      query: (invoiceId) => `/invoice-related/${invoiceId}`,
      providesTags: (result, error, invoiceId) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: 'pay-orders' as const, id })),
            { type: 'pay-orders', id: `invoice-related-${invoiceId}` }
          ]
          : [{ type: 'pay-orders', id: `invoice-related-${invoiceId}` }]
    }),

    getInvoicePayOrders: builder.query<PayOrderDto[], number>({
      query: (invoiceId) => `/invoice-payment/${invoiceId}`,
      providesTags: (result, error, invoiceId) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: 'pay-orders' as const, id })),
            { type: 'pay-orders', id: `invoice-paid-${invoiceId}` }
          ]
          : [{ type: 'pay-orders', id: `invoice-paid-${invoiceId}` }]
    })
    
  })
});

export const {
  useGetPayOrdersQuery,
  useGetPayOrderQuery,
  useCreatePayOrderMutation,
  useCreateTransferPayOrderMutation,
  useUpdatePayOrderMutation,
  useUpdatePayOrderStatusMutation,  
  useDeletePayOrderMutation,
  useUpdateDatePayOrderMutation,
  useGetInvoiceRelatedPayOrdersQuery,
  useGetInvoicePayOrdersQuery
} = payOrdersApi;
